import type { KeyboardEvent } from 'react';
import { type MutableRefObject, useCallback, useEffect, useRef } from 'react';

export function useCustomSelectKeyDown<T>({
    options,
    selected,
    setSelected,
    onSelect,
    closeDropdown,
    itemRefs,
}: {
    options: T[];
    selected: number;
    setSelected: (index: number) => void;
    onSelect: (option: T) => void;
    closeDropdown: () => void;
    itemRefs: MutableRefObject<(HTMLElement | null)[]>;
}) {
    const selectedRef = useRef(selected);

    useEffect(() => {
        selectedRef.current = selected;
    }, [selected]);

    const scrollToItem = (index: number) => {
        const el = itemRefs.current[index];
        if (el) {
            el.scrollIntoView({ block: 'nearest' });
        }
    };

    return useCallback(
        (e: KeyboardEvent<HTMLElement>) => {
            const key = e.key;
            const current = selectedRef.current;
            const lastIndex = options.length - 1;
            const hasSelection = current >= 0 && current < options.length;

            switch (key) {
                case 'ArrowDown': {
                    const next = (current + 1) % options.length;
                    setSelected(next);
                    scrollToItem(next);
                    e.preventDefault();
                    break;
                }
                case 'ArrowUp': {
                    const prev = current <= 0 ? lastIndex : current - 1;
                    setSelected(prev);
                    scrollToItem(prev);
                    e.preventDefault();
                    break;
                }
                case 'Enter':
                    if (hasSelection) {
                        onSelect(options[current]);
                        closeDropdown();
                        e.preventDefault();
                    }
                    break;
                case 'Tab':
                    if (hasSelection) {
                        onSelect(options[current]);
                        closeDropdown();
                        e.preventDefault();
                    }
                    break;
                case 'Escape':
                    closeDropdown();
                    e.preventDefault();
                    break;
            }
        },
        [options, setSelected, onSelect, closeDropdown],
    );
}
